import { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'

const useFormatters = (valueFormat, labelFormat, categoryFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])
  const categoryFormatter = useCallback(d => formatter(d, categoryFormat), [formatter, categoryFormat])
  const labelFormatter = useCallback(d => formatter(d, labelFormat), [formatter, labelFormat])

  return {
    valueFormatter,
    categoryFormatter,
    labelFormatter
  }
}

/**
 * Component for adding marker circles (milestones) to a time series
 * Can pull data from a DDP pipeline
 * Shows individual circles at specific dates with hover tooltips
 */
function DDPComponentMarkers ({
  seriesId,
  pipeline,
  variables,
  dataAccessor,
  xAccessor,
  yAccessor,
  labelAccessor,
  colorAccessor,
  valueFormat,
  categoryFormat,
  labelFormat,
  defaultColor,
  symbolSize,
  hoverSymbolSize,
  symbol,
  showLabel,
  labelPosition,
  label,
  itemStyle,
  emphasis,
  tooltip,
  zlevel,
  z
}) {
  const { valueFormatter, categoryFormatter, labelFormatter } = useFormatters(valueFormat, labelFormat, categoryFormat)
  const { setSeriesLoading, setSeries } = useChartContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  const scatterData = useMemo(() => {
    if (!data) return []

    const markerData = get(data, dataAccessor) || data

    if (!Array.isArray(markerData)) {
      console.warn('DDPComponentMarkers: data is not an array', markerData)
      return []
    }

    return markerData.map(item => {
      const xValue = get(item, xAccessor)
      const yValue = yAccessor ? get(item, yAccessor) : 0
      const label = labelAccessor ? get(item, labelAccessor) : ''
      const color = colorAccessor ? get(item, colorAccessor) : defaultColor

      return {
        value: [xValue, yValue],
        label: label,
        itemStyle: {
          color: color,
          ...itemStyle
        },
        // Store original item for tooltip formatting
        _raw: item
      }
    })
  }, [data, dataAccessor, xAccessor, yAccessor, labelAccessor, colorAccessor, defaultColor, itemStyle])

  useEffect(() => {
    setSeriesLoading(seriesId, isLoading)
  }, [seriesId, isLoading, setSeriesLoading])

  // Create or update a scatter series for the markers
  useEffect(() => {
    if (!isLoading && scatterData.length > 0) {
      setSeries(seriesId, {
        type: 'scatter',
        id: seriesId,
        name: seriesId,
        data: scatterData,
        symbol: symbol,
        symbolSize: symbolSize,
        itemStyle: itemStyle,
        zlevel: zlevel,
        z: z,
        label: {
          show: showLabel,
          position: labelPosition,
          formatter: (params) => {
            return params.data.label || ''
          },
          ...label
        },
        emphasis: {
          scale: true,
          symbolSize: hoverSymbolSize,
          label: {
            show: false,
            position: 'top',
            formatter: (params) => {
              return labelFormatter(params.data.label || '')
            },
            ...emphasis?.label
          },
          ...emphasis
        },
        tooltip: {
          show: true,
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#ccc',
          borderWidth: 1,
          borderRadius: 8,
          padding: [8, 12],
          textStyle: {
            color: '#333'
          },
          formatter: (params) => {
            const date = categoryFormatter(params.value[0])
            const label = labelFormatter(params.data.label)
            return `
              <div style="line-height: 1.5;">
                <div style="font-weight: 600; margin-bottom: 4px; font-size: 14px;">${label}</div>
                <div style="font-size: 12px; color: #666;">${date}</div>
              </div>
            `
          },
          ...tooltip
        }
      })
    }
  }, [seriesId, isLoading, scatterData, setSeries, categoryFormatter, valueFormatter, symbolSize, hoverSymbolSize, symbol, showLabel, labelPosition, label, itemStyle, emphasis, tooltip, zlevel, z, labelFormatter])

  return null
}

DDPComponentMarkers.propTypes = {
  /** Unique identifier for this marker series */
  seriesId: PropTypes.string.isRequired,

  /** The data pipeline to execute */
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),

  /** Pipeline variables for data fetching */
  variables: PropTypes.object,

  /** Accessor path to the array of marker data (e.g., 'markers.data') */
  dataAccessor: PropTypes.string,

  /** Accessor for the x-axis value (date) in each marker object */
  xAccessor: PropTypes.string,

  /** Accessor for the y-axis value in each marker object (optional, defaults to 0) */
  yAccessor: PropTypes.string,

  /** Accessor for the label text in each marker object */
  labelAccessor: PropTypes.string,

  /** Accessor for the color in each marker object */
  colorAccessor: PropTypes.string,

  /** Format string for values */
  valueFormat: PropTypes.string,

  /** Format string for labels */
  labelFormat: PropTypes.string,

  /** Format string for categories/dates */
  categoryFormat: PropTypes.string,

  /** Default color if colorAccessor doesn't find a color */
  defaultColor: PropTypes.string,

  /** Size of the marker circles */
  symbolSize: PropTypes.number,

  /** Size of the marker circles on hover */
  hoverSymbolSize: PropTypes.number,

  /** Symbol type (circle, rect, triangle, etc.) */
  symbol: PropTypes.string,

  /** Whether to show labels by default */
  showLabel: PropTypes.bool,

  /** Position of the label */
  labelPosition: PropTypes.string,

  /** ECharts label configuration (merged with defaults) */
  label: PropTypes.object,

  /** ECharts itemStyle configuration (merged with per-item colors) */
  itemStyle: PropTypes.object,

  /** ECharts emphasis (hover) configuration (merged with defaults) */
  emphasis: PropTypes.object,

  /** ECharts tooltip configuration (merged with defaults) */
  tooltip: PropTypes.object,

  /** Z-level of the series */
  zlevel: PropTypes.number,

  /** Z-index of the series */
  z: PropTypes.number
}

DDPComponentMarkers.defaultProps = {
  dataAccessor: 'data',
  xAccessor: 'date',
  yAccessor: null,
  labelAccessor: 'label',
  colorAccessor: 'color',
  labelFormat: 'title',
  valueFormat: 'marketValue',
  categoryFormat: 'date',
  defaultColor: '#5B91F4',
  symbolSize: 20,
  hoverSymbolSize: 32,
  symbol: 'circle',
  showLabel: false,
  labelPosition: 'top',
  label: {},
  itemStyle: {},
  emphasis: {},
  tooltip: {},
  zlevel: 0,
  z: 2
}

export default DDPComponentMarkers
